import React, { useState, useEffect, useCallback, useRef } from "react";
import useAuth from "../hooks/useAuth";
import useClickAndHold from "../hooks/useClickAndHold";
import useContactQueue from "../hooks/useContactQueue";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

/**
 * Format an ISO-8601 timestamp (with offset) into a short, friendly local-time
 * string like "Wed 8:00 AM, Apr 22". Used for the quiet-hours "next allowed send"
 * hint so texters know when they can try again. Falls back gracefully on bad input.
 */
function formatNextAllowed(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}

// Quiet-hours window matches backend defaults. If you change these in
// app/config.py, update here too.
const QUIET_HOUR_START = 21; // 9 PM — send window closes
const QUIET_HOUR_END = 8;    // 8 AM — send window opens

// Enter-hold auto-send pacing (module scope = stable across renders).
const AUTO_SEND_THRESHOLD_MS = 200; // ignore an accidental Enter tap
const AUTO_SEND_INTERVAL_MS = 550;  // pacing between sends (>= send cooldown)

/**
 * Determine whether sending to a campaign is currently blocked by quiet hours,
 * based on the browser's current time interpreted in the campaign's IANA
 * timezone. Uses Intl.DateTimeFormat so no server round-trip is needed.
 *
 * Returns:
 *   { inQuietHours: boolean, localHour: number, nextAllowedISO: string|null }
 *
 * The backend is the source of truth — this frontend check is a proactive
 * UX aid. If clock skew makes us wrong at the boundary, the server's 422
 * response still catches it via the safety-net error banner.
 */
function getQuietHoursStatus(timezone, now = new Date()) {
  if (!timezone) return { inQuietHours: false, localHour: null, nextAllowedISO: null };

  try {
    // Pull hour + date parts directly in the campaign's timezone.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const hour = parseInt(partMap.hour, 10);
    const inQuietHours = hour >= QUIET_HOUR_START || hour < QUIET_HOUR_END;
    if (!inQuietHours) {
      return { inQuietHours: false, localHour: hour, nextAllowedISO: null };
    }

    // Compute next allowed send in the campaign's local timezone. Evening →
    // tomorrow's 8 AM; early morning → today's 8 AM. We build the target
    // local timestamp then reinterpret it in the target zone to get a correct
    // UTC instant. This avoids DST pitfalls better than naive arithmetic.
    const y = parseInt(partMap.year, 10);
    const m = parseInt(partMap.month, 10);
    let d = parseInt(partMap.day, 10);
    if (hour >= QUIET_HOUR_START) {
      // Evening: roll to tomorrow.
      const tomorrow = new Date(Date.UTC(y, m - 1, d + 1));
      d = tomorrow.getUTCDate();
    }
    // Build an ISO-like local string; Date will interpret it as the browser's
    // local time, but for display via formatNextAllowed that's fine — the
    // backend's server-computed scheduled_at is what actually governs dispatch.
    const nextAllowed = new Date(
      `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T${String(QUIET_HOUR_END).padStart(2, "0")}:00:00`,
    );
    return {
      inQuietHours: true,
      localHour: hour,
      nextAllowedISO: nextAllowed.toISOString(),
    };
  } catch {
    // Invalid timezone — fail open (let the server reject the send).
    return { inQuietHours: false, localHour: null, nextAllowedISO: null };
  }
}

export default function SendingConsole() {
  const { user } = useAuth();

  // Campaign & script state
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [autoRotate, setAutoRotate] = useState(true);

  // Message state
  const [messageBody, setMessageBody] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [showMmsField, setShowMmsField] = useState(false);

  // UI state
  const [sendLog, setSendLog] = useState([]);
  // error is either null or an object: { type: "generic" | "quiet_hours", ... }.
  // A structured shape lets us render quiet-hours blocks with context (local time,
  // timezone, next allowed send) instead of surfacing the raw "quiet_hours" token.
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  // Quiet-hours status for the selected campaign. Recomputed every 60s so
  // the UI swaps buttons automatically at the 9 PM boundary without requiring
  // a page refresh. { inQuietHours, localHour, nextAllowedISO }
  const [quietHours, setQuietHours] = useState({
    inQuietHours: false, localHour: null, nextAllowedISO: null,
  });

  // Refs
  const textareaRef = useRef(null);

  // Contact queue hook
  const {
    currentContact,
    currentCC,
    currentScript,
    conversationHistory,
    queueStats,
    queueExhausted,
    loading: queueLoading,
    skipContact,
    advanceAfterSend,
    personalizeBody,
  } = useContactQueue({
    campaignId: selectedCampaign?.id,
    onQueueEmpty: () => {},
  });

  // Load active campaigns
  useEffect(() => {
    api.get("/campaigns?status=active")
      .then((res) => setCampaigns(res.data.campaigns || []))
      .catch(() => {});
  }, []);

  // Load scripts when campaign changes
  useEffect(() => {
    if (!selectedCampaign) return;

    api.get(`/campaigns/${selectedCampaign.id}/scripts`)
      .then((res) => {
        const loadedScripts = res.data.scripts || [];
        setScripts(loadedScripts);
        if (loadedScripts.length > 0) {
          setSelectedScript(loadedScripts[0]);
        }
      })
      .catch(() => {});
  }, [selectedCampaign]);

  // Re-evaluate quiet-hours status whenever the campaign changes, and then
  // every 60 seconds so the UI swaps from "Hold to Send" to "Hold to Queue"
  // (or vice versa) at the 9 PM / 8 AM boundary without a page refresh.
  useEffect(() => {
    const refresh = () => setQuietHours(getQuietHoursStatus(selectedCampaign?.timezone));
    refresh();
    if (!selectedCampaign?.timezone) return;
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [selectedCampaign?.timezone]);

  // When contact or script changes, personalize the message
  useEffect(() => {
    if (!currentContact) return;

    // If server assigned a script via A/B rotation, use it
    const scriptToUse = autoRotate && currentScript ? currentScript : selectedScript;
    if (scriptToUse) {
      // If auto-rotate and server gave us a different script, sync selection
      if (autoRotate && currentScript && currentScript.id !== selectedScript?.id) {
        setSelectedScript(currentScript);
      }
      setMessageBody(personalizeBody(scriptToUse.body));
    }
  }, [currentContact, currentScript]); // eslint-disable-line

  // Handle send
  const handleSend = useCallback(
    async ({ holdDurationMs, inputMethod, bodyOverride }) => {
      const body = bodyOverride != null ? bodyOverride : messageBody;
      if (!currentContact || !selectedCampaign || !body.trim()) return { ok: false };

      setSending(true);
      setError(null);

      try {
        const payload = {
          campaign_id: selectedCampaign.id,
          contact_id: currentContact.id,
          body: body,
          hold_duration_ms: holdDurationMs,
          input_method: inputMethod,
          script_id: selectedScript?.id,
        };

        if (mediaUrl.trim()) {
          payload.media_url = mediaUrl.trim();
        }

        const res = await api.post("/messages/send", payload);

        // Update send log
        setSendLog((prev) => [
          {
            id: res.data.message?.id || Date.now(),
            contactId: currentContact.id,
            phone: currentContact.phone_number,
            name: `${currentContact.first_name || ""} ${currentContact.last_name || ""}`.trim(),
            body: body,
            time: new Date().toLocaleTimeString(),
            script: selectedScript?.label || selectedScript?.name || "—",
            status: "sent",
            scheduledAt: null,
          },
          ...prev.slice(0, 49),
        ]);

        // Clear MMS
        setMediaUrl("");

        // Advance to next contact
        await advanceAfterSend();
        return { ok: true };
      } catch (err) {
        const status = err.response?.status;
        const data = err.response?.data || {};
        if (status === 422 && data.error === "quiet_hours") {
          // Structured quiet-hours block — API sends local_time ("22:14"),
          // campaign_timezone (IANA), and next_allowed_at (ISO-8601 with offset).
          setError({
            type: "quiet_hours",
            localTime: data.local_time,
            campaignTimezone: data.campaign_timezone,
            nextAllowedAt: data.next_allowed_at,
          });
        } else if (status === 422 && data.error === "invalid_campaign_timezone") {
          setError({
            type: "generic",
            message:
              data.message ||
              "This campaign has an invalid timezone — ask an admin to fix it.",
          });
        } else {
          // Phase 42 (F3): map backend error code to user-safe text via
          // errorToUserMessage; never echo raw 500/code strings here.
          setError({
            type: "generic",
            message: errorToUserMessage(err, "Failed to send message"),
          });
        }
        return { ok: false };
      } finally {
        setSending(false);
      }
    },
    [currentContact, selectedCampaign, messageBody, selectedScript, mediaUrl, advanceAfterSend]
  );

  // ── Enter-hold continuous send ────────────────────────────────────────
  // Compliance-approved (2026): an unbroken manual hold counts as the
  // individual human action for each message. Holding Enter sends the current
  // contact, auto-advances, and keeps sending down the queue — paced by the
  // same cooldown as a single send — until the key is RELEASED, the queue
  // empties, quiet hours block, or a send errors. Each message still records
  // its own hold_duration_ms + input_method="keyboard" for the P2P audit
  // trail. Key-repeat keydowns are ignored; a keyup (or losing window focus)
  // stops the run immediately.
  const autoHeldRef = useRef(false);
  const autoRunningRef = useRef(false);
  const [autoSending, setAutoSending] = useState(false);

  // Mirror what the async loop reads so it never sees stale closure state.
  const liveRef = useRef({});
  useEffect(() => {
    liveRef.current = { currentContact, messageBody, queueExhausted, handleSend, handleQueue, quietHours, selectedScript, personalizeBody };
  });

  const runAutoSend = useCallback(async () => {
    if (autoRunningRef.current) return;
    autoRunningRef.current = true;
    setAutoSending(true);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      // Require the key to stay down briefly before the first send so a quick
      // tap of Enter never fires a message.
      const held0 = Date.now();
      while (autoHeldRef.current && Date.now() - held0 < AUTO_SEND_THRESHOLD_MS) {
        await sleep(25);
      }
      let prevSend = Date.now() - AUTO_SEND_THRESHOLD_MS;
      while (autoHeldRef.current) {
        const s = liveRef.current;
        if (!s.currentContact || s.queueExhausted) break;
        // Personalize fresh from the CURRENT contact+script at send time, so a
        // just-advanced contact can never receive the previous contact's text.
        const outBody = s.selectedScript
          ? s.personalizeBody(s.selectedScript.body)
          : (s.messageBody || "");
        if (!outBody.trim()) break;
        // Respect quiet hours: queue for the next window instead of firing a
        // send the server will reject.
        const action = s.quietHours && s.quietHours.inQuietHours ? s.handleQueue : s.handleSend;
        const holdMs = Math.max(AUTO_SEND_THRESHOLD_MS, Date.now() - prevSend);
        const res = await action({ holdDurationMs: holdMs, inputMethod: "keyboard", bodyOverride: outBody });
        prevSend = Date.now();
        if (!res || !res.ok || !autoHeldRef.current) break;
        await sleep(AUTO_SEND_INTERVAL_MS);
      }
    } finally {
      autoRunningRef.current = false;
      setAutoSending(false);
    }
  }, []);

  const startAutoHold = useCallback(() => {
    if (autoHeldRef.current) return; // ignore auto-repeat keydowns
    autoHeldRef.current = true;
    setError(null);
    runAutoSend();
  }, [runAutoSend]);

  const stopAutoHold = useCallback(() => {
    autoHeldRef.current = false;
  }, []);

  // Queue a message for the next send window (used during quiet hours).
  // Same payload as /send; server computes scheduled_at. Still requires a
  // click-and-hold gesture because P2P compliance is about the human
  // initiating each individual message — whether it goes out now or later.
  const handleQueue = useCallback(
    async ({ holdDurationMs, inputMethod, bodyOverride }) => {
      const body = bodyOverride != null ? bodyOverride : messageBody;
      if (!currentContact || !selectedCampaign || !body.trim()) return { ok: false };

      setSending(true);
      setError(null);

      try {
        const payload = {
          campaign_id: selectedCampaign.id,
          contact_id: currentContact.id,
          body: body,
          hold_duration_ms: holdDurationMs,
          input_method: inputMethod,
          script_id: selectedScript?.id,
        };
        if (mediaUrl.trim()) payload.media_url = mediaUrl.trim();

        const res = await api.post("/messages/queue", payload);

        setSendLog((prev) => [
          {
            id: res.data.message?.id || Date.now(),
            contactId: currentContact.id,
            phone: currentContact.phone_number,
            name: `${currentContact.first_name || ""} ${currentContact.last_name || ""}`.trim(),
            body: body,
            time: new Date().toLocaleTimeString(),
            script: selectedScript?.label || selectedScript?.name || "—",
            status: "queued",
            scheduledAt: res.data.scheduled_for || null,
          },
          ...prev.slice(0, 49),
        ]);

        setMediaUrl("");
        await advanceAfterSend();
        return { ok: true };
      } catch (err) {
        const status = err.response?.status;
        const data = err.response?.data || {};
        if (status === 409 && data.error === "already_scheduled") {
          setError({
            type: "generic",
            message: "This contact already has a message queued. Cancel the existing one before re-queueing.",
          });
        } else {
          setError({
            type: "generic",
            message: data.error || data.message || "Failed to queue message",
          });
        }
        return { ok: false };
      } finally {
        setSending(false);
      }
    },
    [currentContact, selectedCampaign, messageBody, selectedScript, mediaUrl, advanceAfterSend],
  );

  // Cancel a previously-queued message. Optimistically flips the log entry
  // to "canceled" on success; surfaces 403/409 errors in place.
  const handleCancel = useCallback(async (messageId) => {
    if (!messageId) return;
    try {
      await api.post(`/messages/${messageId}/cancel-scheduled`);
      setSendLog((prev) =>
        prev.map((e) => (e.id === messageId ? { ...e, status: "canceled" } : e)),
      );
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      if (status === 403) {
        setError({
          type: "generic",
          message: "You can only cancel messages you queued yourself (or ask a manager).",
        });
      } else if (status === 409) {
        setError({
          type: "generic",
          message: "This message can no longer be canceled — it may have already been sent.",
        });
      } else {
        setError({
          type: "generic",
          message: data.error || data.message || "Failed to cancel message",
        });
      }
    }
  }, []);

  // Two independent click-and-hold instances so each button preserves its own
  // animation/cooldown state. During quiet hours we render the queue button in
  // the main action slot; otherwise the send button.
  const sendHold = useClickAndHold({
    threshold: 200,
    onSend: handleSend,
    cooldown: 500,
  });
  const queueHold = useClickAndHold({
    threshold: 200,
    onSend: handleQueue,
    cooldown: 500,
  });

  // Script change handler
  const handleScriptChange = (script) => {
    setSelectedScript(script);
    setAutoRotate(false); // Manual selection disables auto-rotate
    if (currentContact) {
      setMessageBody(personalizeBody(script.body));
    } else {
      setMessageBody(script.body);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const typingInField = () => {
      const el = document.activeElement;
      return (
        el === textareaRef.current ||
        (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable))
      );
    };
    const handleKeyDown = (e) => {
      // Ctrl+Enter or Cmd+Enter: focus send button
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        document.getElementById("send-btn")?.focus();
        return;
      }

      // Ctrl+S or Cmd+S: skip contact
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        skipContact();
        return;
      }

      // Enter (held, no modifiers, not typing in a field): continuous
      // auto-send down the queue for as long as the key stays down.
      if (
        e.key === "Enter" &&
        !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey &&
        !typingInField()
      ) {
        e.preventDefault();
        startAutoHold(); // internally ignores auto-repeat keydowns
        return;
      }

      // Tab (when not in textarea): cycle scripts
      if (e.key === "Tab" && document.activeElement !== textareaRef.current && scripts.length > 1) {
        e.preventDefault();
        const idx = scripts.findIndex((s) => s.id === selectedScript?.id);
        const next = scripts[(idx + 1) % scripts.length];
        handleScriptChange(next);
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === "Enter") stopAutoHold();
    };
    // Safety: if the tab loses focus mid-hold a keyup may never arrive, so
    // stop auto-send — it must never run unattended.
    const handleBlur = () => stopAutoHold();

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleBlur);
    };
  }, [scripts, selectedScript, skipContact, startAutoHold, stopAutoHold]); // eslint-disable-line

  // Segment calculation
  const charCount = messageBody.length;
  const segmentCount = charCount <= 160 ? 1 : Math.ceil(charCount / 153);
  const isLongMessage = segmentCount > 1;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Sending Console</h1>
        {queueStats && (
          <div className="flex items-center space-x-3 text-sm">
            <span className="text-green-600 font-semibold">{queueStats.sent} sent</span>
            <span className="text-gray-300">|</span>
            <span className="text-blue-600 font-semibold">{queueStats.replied} replied</span>
            <span className="text-gray-300">|</span>
            <span className="text-red-500 font-semibold">{queueStats.opted_out} opted out</span>
            <span className="text-gray-300">|</span>
            <span className="text-yellow-600 font-semibold">{queueStats.skipped} skipped</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-700 font-bold">{queueStats.pct_complete}% done</span>
          </div>
        )}
      </div>

      {/* Campaign selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Campaign</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedCampaign?.id || ""}
              onChange={(e) => {
                const c = campaigns.find((c) => c.id === parseInt(e.target.value));
                setSelectedCampaign(c || null);
                setSendLog([]);
                setError(null);
              }}
            >
              <option value="">Select a campaign...</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Queue progress bar */}
          {queueStats && (
            <div className="w-64">
              <label className="block text-xs font-medium text-gray-500 mb-1">Queue Progress</label>
              <div className="relative">
                <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${queueStats.pct_complete}%` }}
                  />
                </div>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
                  {queueStats.pending} remaining
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedCampaign && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left column: Contact + Compose (7 cols) */}
          <div className="lg:col-span-7 space-y-4">

            {/* Contact card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  Current Contact
                </h3>
                <div className="flex items-center space-x-2">
                  {autoRotate && scripts.length > 1 && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      A/B Auto
                    </span>
                  )}
                  <button
                    onClick={skipContact}
                    disabled={!currentContact || queueLoading}
                    className="text-sm text-gray-400 hover:text-blue-600 font-medium transition-colors disabled:opacity-30"
                    title="Skip (Ctrl+S)"
                  >
                    Skip &rarr;
                  </button>
                </div>
              </div>

              {currentContact ? (
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-lg font-bold text-gray-900">
                      {currentContact.first_name} {currentContact.last_name}
                    </p>
                    <p className="text-sm text-gray-500 font-mono">{currentContact.phone_number}</p>
                    {/* Voter data row */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {currentContact.party && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          currentContact.party === "DEM" ? "bg-blue-100 text-blue-700" :
                          currentContact.party === "REP" ? "bg-red-100 text-red-700" :
                          "bg-gray-100 text-gray-600"
                        }`}>
                          {currentContact.party}
                        </span>
                      )}
                      {currentContact.city && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {currentContact.city}{currentContact.state ? `, ${currentContact.state}` : ""}
                        </span>
                      )}
                      {currentContact.county && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {currentContact.county} County
                        </span>
                      )}
                      {currentContact.van_id && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-mono">
                          VAN: {currentContact.van_id}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Conversation history indicator */}
                  {conversationHistory.length > 0 && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                      {conversationHistory.length} prior msg{conversationHistory.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              ) : queueExhausted ? (
                <div className="text-center py-6">
                  <p className="text-2xl mb-2">&#127881;</p>
                  <p className="text-lg font-semibold text-gray-700">Queue Complete!</p>
                  <p className="text-sm text-gray-400 mt-1">All contacts in this campaign have been reached.</p>
                </div>
              ) : (
                <div className="py-4">
                  <div className="animate-pulse flex space-x-4">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              )}
            </div>

            {/* Conversation history (if any) */}
            {conversationHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
                  Conversation History
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {conversationHistory.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                          msg.direction === "outbound"
                            ? "bg-blue-100 text-blue-900"
                            : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        <p>{msg.body}</p>
                        <p className="text-xs opacity-50 mt-1">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Script selector */}
            {scripts.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Script Variant
                  </h3>
                  {scripts.length > 1 && (
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoRotate}
                        onChange={(e) => setAutoRotate(e.target.checked)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-xs text-gray-500">A/B auto-rotate</span>
                    </label>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {scripts.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleScriptChange(s)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedScript?.id === s.id
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {s.label || s.name}
                      {s.ab_weight !== undefined && (
                        <span className="ml-1 opacity-70">({s.ab_weight || s.weight}%)</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message composer */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                Message
              </h3>
              <textarea
                ref={textareaRef}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={4}
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder="Type your message..."
                disabled={!currentContact}
              />

              {/* MMS field */}
              {showMmsField && (
                <div className="mt-2">
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder="Media URL (https://...)"
                  />
                </div>
              )}

              {/* Script-level attachment indicator. The backend attaches the
                  campaign message's media automatically on send (explicit
                  + MMS input above overrides it), so tell the texter an
                  image/video is riding along. */}
              {selectedScript?.media_url && !mediaUrl.trim() && (
                <p className="mt-1.5 text-xs text-gray-500">
                  📎 This message includes{" "}
                  <a
                    href={selectedScript.media_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    attached media
                  </a>
                  {" "}— it sends automatically with each message.
                </p>
              )}

              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center space-x-3">
                  <span className={`text-xs ${isLongMessage ? "text-amber-500 font-medium" : "text-gray-400"}`}>
                    {charCount} chars &middot; {segmentCount} segment{segmentCount !== 1 ? "s" : ""}
                  </span>
                  <button
                    onClick={() => setShowMmsField(!showMmsField)}
                    className={`text-xs font-medium transition-colors ${
                      showMmsField ? "text-blue-600" : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {showMmsField ? "Hide MMS" : "+ MMS"}
                  </button>
                </div>

                {/* Action button — swaps between "Hold to Send" and
                    "Hold to Queue" depending on whether the selected
                    campaign is currently in quiet hours. The server is
                    still the source of truth; this is a proactive UX aid. */}
                {quietHours.inQuietHours ? (
                  <button
                    id="send-btn"
                    {...queueHold.bindHold}
                    disabled={!currentContact || !messageBody.trim() || sending || queueHold.isCoolingDown}
                    className={`relative px-10 py-3 rounded-xl font-bold text-white transition-all select-none text-sm ${
                      !currentContact || !messageBody.trim() || sending || queueHold.isCoolingDown
                        ? "bg-gray-300 cursor-not-allowed"
                        : queueHold.isReady
                        ? "bg-amber-500 shadow-lg shadow-amber-200 scale-105"
                        : queueHold.isHolding
                        ? "bg-amber-700 shadow-lg shadow-amber-200"
                        : "bg-amber-600 hover:bg-amber-700 shadow-sm"
                    }`}
                  >
                    {queueHold.isHolding && (
                      <div
                        className="absolute inset-0 bg-amber-400 rounded-xl transition-all"
                        style={{ width: `${queueHold.progress * 100}%`, opacity: 0.3 }}
                      />
                    )}
                    <span className="relative z-10">
                      {sending
                        ? "Queueing..."
                        : queueHold.isCoolingDown
                        ? "Queued \u2713"
                        : queueHold.isReady
                        ? "Release to Queue"
                        : queueHold.isHolding
                        ? `Hold... ${Math.round(queueHold.progress * 100)}%`
                        : "Hold to Queue for 8 AM"}
                    </span>
                  </button>
                ) : (
                  <button
                    id="send-btn"
                    {...sendHold.bindHold}
                    disabled={!currentContact || !messageBody.trim() || sending || sendHold.isCoolingDown}
                    className={`relative px-10 py-3 rounded-xl font-bold text-white transition-all select-none text-sm ${
                      !currentContact || !messageBody.trim() || sending || sendHold.isCoolingDown
                        ? "bg-gray-300 cursor-not-allowed"
                        : sendHold.isReady
                        ? "bg-green-500 shadow-lg shadow-green-200 scale-105"
                        : sendHold.isHolding
                        ? "bg-blue-700 shadow-lg shadow-blue-200"
                        : "bg-blue-600 hover:bg-blue-700 shadow-sm"
                    }`}
                  >
                    {sendHold.isHolding && (
                      <div
                        className="absolute inset-0 bg-green-400 rounded-xl transition-all"
                        style={{ width: `${sendHold.progress * 100}%`, opacity: 0.3 }}
                      />
                    )}
                    <span className="relative z-10">
                      {sending
                        ? "Sending..."
                        : sendHold.isCoolingDown
                        ? "Sent \u2713"
                        : sendHold.isReady
                        ? "Release to Send"
                        : sendHold.isHolding
                        ? `Hold... ${Math.round(sendHold.progress * 100)}%`
                        : "Hold to Send"}
                    </span>
                  </button>
                )}
              </div>
              {/* Quiet-hours banner (informational, sits below the button
                  when we're in the quiet-hours window). Explains why the
                  button swap happened so the texter isn't surprised. */}
              {quietHours.inQuietHours && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-lg px-3 py-2 mt-3">
                  <span className="font-semibold">Quiet hours</span> — it's
                  {" "}
                  {quietHours.localHour != null && (
                    <span className="font-mono font-medium">
                      {String(quietHours.localHour).padStart(2, "0")}:00
                    </span>
                  )}
                  {" "}in {selectedCampaign?.timezone}. Direct sending resumes at 8:00 AM
                  local. Queue messages with the amber button above and
                  they'll dispatch automatically when the window opens.
                </div>
              )}
              {error && error.type === "quiet_hours" && (
                <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg px-4 py-3 mt-3">
                  <p className="text-sm font-semibold mb-1">Send blocked — quiet hours</p>
                  <p className="text-xs text-amber-800">
                    It's{" "}
                    <span className="font-mono font-medium">{error.localTime}</span> in{" "}
                    <span className="font-medium">{error.campaignTimezone}</span>. This
                    campaign can only send between 8 AM and 9 PM local time.
                  </p>
                  {error.nextAllowedAt && (
                    <p className="text-xs text-amber-700 mt-1">
                      Next allowed send:{" "}
                      <span className="font-medium">
                        {formatNextAllowed(error.nextAllowedAt)}
                      </span>
                    </p>
                  )}
                </div>
              )}
              {error && error.type === "generic" && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 mt-3">
                  {error.message}
                </div>
              )}
            </div>

            {/* Keyboard shortcuts help */}
            <div className="text-xs text-gray-400 flex items-center space-x-4">
              <span><strong>Space</strong> hold to send</span>
              <span><strong>Ctrl+S</strong> skip contact</span>
              <span><strong>Ctrl+Enter</strong> focus send</span>
              <span><strong>Enter</strong> (hold) auto-send</span>
              {scripts.length > 1 && <span><strong>Tab</strong> cycle scripts</span>}
              {autoSending && (
                <span className="text-blue-600 font-semibold animate-pulse">&#9679; Auto-sending &mdash; release Enter to stop</span>
              )}
            </div>
          </div>

          {/* Middle column: Send log (3 cols) */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  Send Log
                </h3>
                <span className="text-xs text-gray-400">{sendLog.length} messages</span>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                {sendLog.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-300 text-sm">No messages sent yet</p>
                    <p className="text-gray-300 text-xs mt-1">Messages will appear here as you send</p>
                  </div>
                ) : (
                  sendLog.map((entry) => {
                    // Three rendering states: sent (default/green), queued
                    // (amber with Cancel affordance), canceled (gray muted).
                    const isQueued = entry.status === "queued";
                    const isCanceled = entry.status === "canceled";
                    const containerClass = isCanceled
                      ? "border-b border-gray-50 pb-2 last:border-0 opacity-50"
                      : isQueued
                      ? "border-l-4 border-amber-400 bg-amber-50/50 px-2 py-2 rounded-r"
                      : "border-b border-gray-50 pb-2 last:border-0";
                    const bodyClass = isCanceled
                      ? "text-xs text-gray-400 truncate line-through"
                      : "text-xs text-gray-500 truncate";
                    return (
                      <div key={entry.id} className={containerClass}>
                        <div className="flex justify-between items-center text-xs mb-0.5">
                          <span className="font-medium text-gray-700 truncate max-w-[140px]">
                            {entry.name || entry.phone}
                          </span>
                          <span className="text-gray-400 whitespace-nowrap ml-2">
                            {entry.time}
                          </span>
                        </div>
                        <p className={bodyClass}>{entry.body}</p>
                        {entry.script !== "\u2014" && !isCanceled && (
                          <span className="text-xs text-purple-500">{entry.script}</span>
                        )}
                        {isQueued && (
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-amber-700 font-medium">
                              Queued for {formatNextAllowed(entry.scheduledAt)}
                            </span>
                            <button
                              onClick={() => handleCancel(entry.id)}
                              className="text-[10px] text-amber-700 hover:text-red-600 font-medium underline"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        {isCanceled && (
                          <span className="text-[10px] text-gray-400 italic">Canceled</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right column: Script performance (2 cols) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-4">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
                Script Performance
              </h3>
              <div className="space-y-3">
                {scripts.map((s) => (
                  <div key={s.id} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {s.label || s.name}
                      </span>
                      <span className="text-xs text-gray-400">{s.send_count || 0} sent</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${s.reply_rate || 0}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-green-600">{s.reply_rate || 0}%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{s.reply_count || 0} replies</span>
                      <span className="text-red-400">{s.opt_out_rate || 0}% opt-out</span>
                    </div>
                  </div>
                ))}
                {scripts.length === 0 && (
                  <p className="text-xs text-gray-300 text-center py-4">No scripts configured</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
